<template>
  <Modal
    title="Add/Replace Equipment"
    class-name="equipment-replace-modal"
    :saving="saving"
    @submit="handleSubmit"
    @hide-modal="$emit('hide-modal')"
  >
    <EquipmentWizard
      :equipment="equipment"
      :generic-equipment-type-id="genericEquipmentTypeId"
      @update:replacingType="handleChange('replacingType', $event)"
      @update:replacesEquipmentId="handleChange('replacesEquipmentId', $event)"
      @update:newEquipmentId="handleChange('newEquipmentId', $event)"
      @update:replacingComponentIds="
        handleChange('replacingComponentIds', $event)
      "
    >
      <template #equipmentForm="{ equipmentTypeId }">
        <div class="bg-white p-3">
          <EquipmentForm
            ref="equipmentForm"
            :address="address"
            :customer-id="currentUser.id"
            :equipment-type-id-filter="equipmentTypeId"
            :generic-equipment-type-id="genericEquipmentTypeId"
          />
        </div>
      </template>
      <template #componentForm="{ component }">
        <div v-if="component" class="bg-white">
          <EquipmentComponentForm
            :ref="`componentForm_${component.id}`"
            :model-value="component"
            :use-same-manufacturer="false"
            :show-use-same-manufacturer="false"
          />
        </div>
      </template>
    </EquipmentWizard>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Modal from "@/components/Modal.vue";
import EquipmentComponentForm from "@/Equipment/EquipmentComponentForm.vue";
import EquipmentForm from "@/Equipment/EquipmentForm.vue";
import { fullAddress } from "@/mixins";

export default {
  components: {
    Modal,
    EquipmentComponentForm,
    EquipmentForm
  },
  props: {
    equipment: {
      type: Array,
      default: () => []
    },
    address: {
      type: Object,
      default: null
    },
    equipmentLink: {
      type: Object,
      default: null
    }
  },
  emits: ["hide-modal"],
  data() {
    return {
      replacesEquipmentId: null,
      newEquipmentId: null,
      replacingType: null,
      replacingComponentIds: [],
      saving: false,
      MODAL_MIN_HEIGHT: 750
    };
  },
  computed: {
    ...mapState({
      currentUser: "currentUser",
      genericEquipmentTypeId({ equipmentTypes }) {
        return (equipmentTypes.find((type) => type.is_default) || {}).id;
      }
    })
  },
  methods: {
    ...mapActions([
      "createEquipment",
      "replaceEquipment",
      "replaceEquipmentComponents"
    ]),
    fullAddress,
    handleSubmit() {
      this.saving = true;
      try {
        // if newEquipmentId === -1 that means the user is replacing existing equipment with a new piece of equipment
        if (
          this.replacingType === "equipment" &&
          typeof this.newEquipmentId === "number" &&
          this.newEquipmentId !== -1
        ) {
          // Are we replacing with existing?
          this.handleReplaceEquipment(
            this.replacesEquipmentId,
            this.newEquipmentId
          );
        } else if (this.replacingType === "components") {
          // Are we replacing components?
          this.handleReplaceComponents(this.replacesEquipmentId);
        } else {
          // Are we replacing with new?
          // Are we adding equipment?
          this.handleCreate();
        }
      } catch (e) {
        console.error(e);
      }
      this.saving = false;
    },
    async handleCreate() {
      const payload = await this.$refs.equipmentForm.getRequestData();
      if (payload) {
        if (this.replacesEquipmentId) {
          payload.replaces_equipment_id = this.replacesEquipmentId;
        }
        try {
          const equipment = await this.createEquipment({
            link: this.address.links.equipment_create,
            data: payload
          });
          await this.attachEquipment(equipment.id);
          this.$emit("hide-modal");
        } catch (e) {
          console.error(e);
        }
      }
    },
    async handleReplaceEquipment(replacesEquipmentId, newEquipmentId) {
      try {
        // use the replace link on equipment and pass in new_equipment_id
        const equipment = this.equipment.find(
          (eq) => eq.id === replacesEquipmentId
        );
        if (equipment) {
          await this.replaceEquipment({
            link: equipment.links.replace,
            customer: this.currentUser,
            data: {
              new_equipment_id: newEquipmentId
            }
          });
          await this.attachEquipment(newEquipmentId);
        }
        this.$emit("hide-modal");
      } catch (e) {
        console.error(e);
      }
    },
    handleReplaceComponents(replacesEquipmentId) {
      // Generate an array of components each with the replaces_equipment_id param
      // scrub data from previous component that should not be saved (equipment_id, id)
      const payload = this.replacingComponentIds.reduce(
        (components, replacesComponentId) => {
          const formData =
            this.$refs[`componentForm_${replacesComponentId}`].formData;
          if (formData) {
            delete formData.id;
            delete formData.equipment_id;
            delete formData.archived;
            delete formData.replace;
            delete formData.component_type_id;
            formData.replaces_component_id = replacesComponentId;
            components.push(formData);
          }
          return components;
        },
        []
      );
      const equipment = this.equipment.find(
        (eq) => eq.id === replacesEquipmentId
      );
      if (equipment) {
        this.replaceEquipmentComponents({
          link: equipment.links.replace_components,
          customer: this.currentUser,
          data: payload
        });
        this.attachEquipment(equipment.id);
        this.$emit("hide-modal");
      }
    },
    async attachEquipment(equipmentId) {
      // Handle updating requested equipment link with equipment id
      // or used to create an optional equipment link with an equipment id
      const emitEventName = this.equipmentLink?.id
        ? "update-link"
        : "create-link";
      this.$emit(emitEventName, {
        ...this.equipmentLink,
        equipment: null,
        equipment_id: equipmentId,
        unit_number: this.equipmentLink?.unit_number || 1
      });
    },
    handleChange(key, value) {
      // Simulate vue 3 multiple v-model
      this[key] = value;
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.card-header) {
  border-bottom: none !important;
}
</style>
